import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { Close, AddOutlined } from '@mui/icons-material';

import { useTranslations } from '@/i18n/use-translations';
import { useGetOrganizationRelationsQuery } from '@/state/queries/organizations/use-get-organization-relations.ts';
import { useGetOrganizationRelationEventsQuery } from '@/state/queries/organizations/use-get-organization-relation-events.ts';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { usePatchOrganizationMembershipRolesMutation } from '@/state/mutations/organizations/use-patch-organization-membership-roles.ts';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { useOrgFeatureKeys } from '@/state/queries/entitlements/use-org-feature-keys';
import {
  ORGANIZATION_BASIC_ROLES,
  ORGANIZATION_ROLES,
  type OrganizationRole,
} from '@/enums/organization-roles.enum.ts';
import {
  organizationBasicRoleLabel,
  organizationRoleLabel,
} from '@/components/organizations/organization-invitation-role-labels';
import type { OrganizationRelationListItemDto } from '@/types/dtos/organizations/response/organization-relation.dto.ts';
import { useNotifications } from '@/hooks/use-notifications';
import PageHeaderBar from '@/components/common/page-header-bar';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useDebounce } from '@/hooks/use-debounce.ts';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import InviteModal from '@/components/organizations/invite-to-organization/invite.modal';
import OrganizationTeamMemberRowMenu from '@/components/organizations/organization-team-member-row-menu';
import {
  ORG_ELEVATED_CATEGORY_ORDER,
  ORG_ELEVATED_SECTION_ROLES,
  normalizeOrgElevatedDraft,
  rolesByOrgElevatedCategory,
  type OrgElevatedCategory,
} from '@/components/organizations/org-elevated-role-categories';

const STATUS_OPTIONS = ['', 'active', 'inactive', 'pending', 'suspended'] as const;

/**
 * Capacity-axis roles: require a seat assignment on the relation.
 * Mirrors backend `QUOTA_KEY_GRANTABLE_ELEVATED_ROLES` (host_default:members entry).
 */
const CAPACITY_AXIS_ROLES = new Set<string>([
  ORGANIZATION_ROLES.CONTENT_MANAGER,
  ORGANIZATION_ROLES.EVENT_PLANNER,
  ORGANIZATION_ROLES.EVENT_ORGANIZER,
]);

/**
 * Feature-axis roles and the feature key that unlocks each one.
 * Mirrors backend `FEATURE_GATED_ELEVATED_ROLES`.
 */
const FEATURE_KEY_FOR_ROLE: Partial<Record<string, string>> = {
  [ORGANIZATION_ROLES.ADMIN]: 'org_admin_role',
  [ORGANIZATION_ROLES.PROCUREMENT]: 'org_procurement_role',
};

type OrganizationTeamListProps = {
  organizationId: string;
};

function memberDisplayName(row: OrganizationRelationListItemDto): string {
  return (
    [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ') || row.user?.primaryEmail || row.user?.id || '—'
  );
}

const BASIC_ROLE_FILTER_VALUES = [ORGANIZATION_BASIC_ROLES.MEMBER, ORGANIZATION_BASIC_ROLES.NETWORK] as const;

const ORG_ELEVATED_FILTER_ROLES: OrganizationRole[] = ORG_ELEVATED_CATEGORY_ORDER.flatMap((c) => [
  ...ORG_ELEVATED_SECTION_ROLES[c],
]);

function formatBasicRolesCell(row: OrganizationRelationListItemDto, t: ReturnType<typeof useTranslations>): string {
  const basics = row.basicRoles ?? [];
  if (basics.length === 0) {
    return '—';
  }
  return basics.map((b) => organizationBasicRoleLabel(b, t)).join(' · ');
}

function formatElevatedRolesCell(row: OrganizationRelationListItemDto, t: ReturnType<typeof useTranslations>): string {
  const elevated = row.elevatedRoles ?? [];
  if (elevated.length === 0) {
    return '—';
  }
  return elevated.map((r) => organizationRoleLabel(r as OrganizationRole, t)).join(' · ');
}

export default function OrganizationTeamList({ organizationId }: OrganizationTeamListProps) {
  const t = useTranslations();
  const notifications = useNotifications();
  const location = useLocation();
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const isLargeScreen = useBreakpointUp('lg');
  const [searchParams, setSearchParams] = useSearchParams();
  const basicRole = searchParams.get('basicRole') ?? '';
  const elevatedRole = searchParams.get('elevatedRole') ?? '';
  const status = searchParams.get('status') ?? '';

  const filters = useMemo(
    () => ({
      basicRole: basicRole || undefined,
      elevatedRole: elevatedRole || undefined,
      status: status || undefined,
    }),
    [basicRole, elevatedRole, status],
  );

  const { data, isLoading, isError } = useGetOrganizationRelationsQuery(organizationId, filters);
  const activeOwnerCount = data?.activeOwnerCount ?? 0;
  const { data: permissions } = usePermissionsQuery();
  const canManageRoles = Boolean(permissions?.organizations?.[organizationId]?.includes('update'));
  const canInvite = Boolean(permissions?.organizations?.[organizationId]?.includes('invite'));
  const patchRoles = usePatchOrganizationMembershipRolesMutation(organizationId);
  const { data: balancesData } = useQuotaBalances('Organization', organizationId);
  const { data: orgFeatureKeys } = useOrgFeatureKeys('Organization', organizationId);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [historyRelationId, setHistoryRelationId] = useState<string | null>(null);
  const { data: eventsData, isLoading: eventsLoading } = useGetOrganizationRelationEventsQuery(
    organizationId,
    historyRelationId ?? undefined,
    !!historyRelationId,
  );

  const [rolesDialogRow, setRolesDialogRow] = useState<OrganizationRelationListItemDto | null>(null);
  const [draftByCategory, setDraftByCategory] = useState<Partial<Record<OrgElevatedCategory, OrganizationRole | null>>>(
    {},
  );
  const [seatDraft, setSeatDraft] = useState<Record<string, boolean>>({});

  const isOrganizationRoute = location.pathname.startsWith('/organization/');

  const currentMenuItem = t('common.team');

  const draftElevatedList = useMemo(
    () =>
      normalizeOrgElevatedDraft(
        ORG_ELEVATED_CATEGORY_ORDER.map((c) => draftByCategory[c]).filter((r): r is OrganizationRole => Boolean(r)),
      ),
    [draftByCategory],
  );

  const rolesDialogDelta = useMemo(() => {
    if (!rolesDialogRow) {
      return { removed: new Set<OrganizationRole>(), added: new Set<OrganizationRole>() };
    }
    const initialNorm = normalizeOrgElevatedDraft((rolesDialogRow.elevatedRoles ?? []) as OrganizationRole[]);
    const removed = new Set(initialNorm.filter((r) => !draftElevatedList.includes(r)));
    const added = new Set(draftElevatedList.filter((r) => !initialNorm.includes(r)));
    return { removed, added };
  }, [rolesDialogRow, draftElevatedList]);

  const hasRolesDialogElevatedChanges = rolesDialogDelta.removed.size > 0 || rolesDialogDelta.added.size > 0;

  const hasSeatDraftChanges = useMemo(() => {
    if (!rolesDialogRow) {
      return false;
    }
    const currentAssigned = new Set((rolesDialogRow.quotaAssignments ?? []).map((a) => a.quotaKey));
    return Object.entries(seatDraft).some(([key, val]) => currentAssigned.has(key) !== val);
  }, [rolesDialogRow, seatDraft]);

  const hasAnyDialogChanges = hasRolesDialogElevatedChanges || hasSeatDraftChanges;

  const isSoleActiveOwnerRow =
    !!rolesDialogRow &&
    activeOwnerCount === 1 &&
    (rolesDialogRow.elevatedRoles ?? []).includes(ORGANIZATION_ROLES.OWNER);

  const filteredRows = useMemo(() => {
    const rows = data?.data ?? [];
    const q = (debouncedSearchTerm ?? '').trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter((row) => {
      const name = memberDisplayName(row).toLowerCase();
      const email = (row.user?.primaryEmail ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [data?.data, debouncedSearchTerm]);

  const openRolesDialog = (row: OrganizationRelationListItemDto) => {
    setRolesDialogRow(row);
    setDraftByCategory(rolesByOrgElevatedCategory((row.elevatedRoles ?? []) as OrganizationRole[]));
    const initialSeats: Record<string, boolean> = {};
    for (const a of row.quotaAssignments ?? []) {
      initialSeats[a.quotaKey] = true;
    }
    setSeatDraft(initialSeats);
  };

  const setFilter = (key: 'basicRole' | 'elevatedRole' | 'status', value: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete('organizationRole');
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const setCategoryElevated = (category: OrgElevatedCategory, role: OrganizationRole | '') => {
    setDraftByCategory((prev) => ({
      ...prev,
      [category]: role === '' ? null : role,
    }));
  };

  const handleSaveRoles = async () => {
    if (!rolesDialogRow) {
      return;
    }
    try {
      const basicRolesMerged = [...new Set([...(rolesDialogRow.basicRoles ?? []), ORGANIZATION_BASIC_ROLES.MEMBER])];
      const currentAssigned = new Set((rolesDialogRow.quotaAssignments ?? []).map((a) => a.quotaKey));
      const seatAssignments = Object.entries(seatDraft)
        .filter(([key, val]) => currentAssigned.has(key) !== val)
        .map(([quotaKey, assigned]) => ({ quotaKey, assigned }));
      await patchRoles.mutateAsync({
        relationId: rolesDialogRow.id,
        body: {
          basicRoles: basicRolesMerged,
          elevatedRoles: draftElevatedList,
          ...(seatAssignments.length > 0 ? { seatAssignments } : {}),
        },
      });
      setRolesDialogRow(null);
    } catch {
      notifications.notifyError(t('organization.team.saveError'));
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchExpanded = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
    });
  };

  const handleSearchCollapse = () => {
    setIsSearchExpanded(false);
    setSearchTerm('');
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSearchCollapse();
    }
  };

  const searchContent = (
    <TextField
      variant="outlined"
      placeholder={t('common.search')}
      value={searchTerm}
      onChange={handleSearchChange}
      onKeyDown={handleSearchKeyDown}
      fullWidth
      size="small"
      inputRef={searchInputRef}
      sx={{
        '& .MuiOutlinedInput-root': {
          'borderRadius': 2,
          'backgroundColor': 'background.default',
          'transition': 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
          },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearchCollapse} size="small" sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const mainContent = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: { xs: 'none', sm: '300px', md: '500px' },
          minWidth: 0,
          flex: { xs: '1 1 auto', sm: '0 1 auto' },
        }}
      >
        {isOrganizationRoute && activeOrganization && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
            }}
          >
            {activeOrganization.name}
          </Typography>
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: isOrganizationRoute ? 400 : 600,
            color: isOrganizationRoute ? 'text.secondary' : 'text.primary',
            flexShrink: 0,
            ml: isOrganizationRoute ? 1 : 0,
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
          }}
        >
          {isOrganizationRoute && activeOrganization ? '/ ' : ''}
          {currentMenuItem}
        </Typography>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
          width: 300,
          ml: 'auto',
          flexShrink: 0,
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          variant="standard"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          size="small"
          sx={{
            '& .MuiInput-root': {
              'transition': 'all 0.2s ease-in-out',
              '&:hover:not(.Mui-disabled):before': {
                borderBottomColor: 'action.active',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        {canInvite ? (
          isLargeScreen ? (
            <Button
              variant="outlined"
              startIcon={<AddOutlined />}
              onClick={() => setInviteModalOpen(true)}
              size="small"
              sx={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {t('common.invite').toUpperCase()}
            </Button>
          ) : (
            <IconButton
              onClick={() => setInviteModalOpen(true)}
              sx={{
                'color': 'text.secondary',
                'flexShrink': 0,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <AddOutlined />
            </IconButton>
          )
        ) : null}
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
          gap: 0.5,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleSearchExpand}
          sx={{
            color: 'text.secondary',
            ml: { xs: 0.5, sm: 1 },
          }}
        >
          <SearchIcon />
        </IconButton>
        {canInvite ? (
          <IconButton
            onClick={() => setInviteModalOpen(true)}
            sx={{
              'color': 'text.secondary',
              'mr': { xs: 0.5, sm: 1 },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <AddOutlined />
          </IconButton>
        ) : null}
      </Box>
    </>
  );

  return (
    <>
      <Stack
        width="100%"
        spacing={0}
        pt={0}
        pb={1}
        bgcolor="background.default"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <PageHeaderBar
          searchExpanded={isSearchExpanded}
          searchContent={searchContent}
          mainContent={mainContent}
          onSearchExpanded={handleSearchExpanded}
        />

        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            flexWrap: 'wrap',
            gap: 2,
            px: { xs: 1, sm: 2 },
            pt: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="team-filter-basic-role">{t('organization.team.filterRelation')}</InputLabel>
            <Select
              labelId="team-filter-basic-role"
              label={t('organization.team.filterRelation')}
              value={basicRole}
              onChange={(e) => setFilter('basicRole', e.target.value)}
            >
              <MenuItem value="">{t('organization.team.filterAllRelations')}</MenuItem>
              {BASIC_ROLE_FILTER_VALUES.map((r) => (
                <MenuItem key={r} value={r}>
                  {organizationBasicRoleLabel(r, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="team-filter-elevated-role">{t('organization.team.filterRole')}</InputLabel>
            <Select
              labelId="team-filter-elevated-role"
              label={t('organization.team.filterRole')}
              value={elevatedRole}
              onChange={(e) => setFilter('elevatedRole', e.target.value)}
            >
              <MenuItem value="">{t('organization.team.filterAllElevatedRoles')}</MenuItem>
              {ORG_ELEVATED_FILTER_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {organizationRoleLabel(r, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="team-filter-status">{t('common.relationStatus')}</InputLabel>
            <Select
              labelId="team-filter-status"
              label={t('common.relationStatus')}
              value={status}
              onChange={(e) => setFilter('status', e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s || 'all'} value={s}>
                  {s ? s : t('common.filterAllStatuses')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Stack
          spacing={2}
          sx={{
            display: { xs: 'flex', lg: 'none' },
            px: { xs: 1, sm: 2 },
            pt: 2,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel id="team-filter-basic-role-sm">{t('organization.team.filterRelation')}</InputLabel>
            <Select
              labelId="team-filter-basic-role-sm"
              label={t('organization.team.filterRelation')}
              value={basicRole}
              onChange={(e) => setFilter('basicRole', e.target.value)}
            >
              <MenuItem value="">{t('organization.team.filterAllRelations')}</MenuItem>
              {BASIC_ROLE_FILTER_VALUES.map((r) => (
                <MenuItem key={r} value={r}>
                  {organizationBasicRoleLabel(r, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="team-filter-elevated-role-sm">{t('organization.team.filterRole')}</InputLabel>
              <Select
                labelId="team-filter-elevated-role-sm"
                label={t('organization.team.filterRole')}
                value={elevatedRole}
                onChange={(e) => setFilter('elevatedRole', e.target.value)}
              >
                <MenuItem value="">{t('organization.team.filterAllElevatedRoles')}</MenuItem>
                {ORG_ELEVATED_FILTER_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {organizationRoleLabel(r, t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="team-filter-status-sm">{t('common.relationStatus')}</InputLabel>
              <Select
                labelId="team-filter-status-sm"
                label={t('common.relationStatus')}
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s || 'all-sm'} value={s}>
                    {s ? s : t('common.filterAllStatuses')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Box
          sx={{
            px: { xs: 0, sm: 2 },
            pt: { xs: 1, sm: 2 },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isError ? (
            <Typography color="error" sx={{ px: { xs: 1, sm: 0 } }}>
              {t('common.noDetail')}
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowX: 'auto',
                overflowY: 'auto',
                maxWidth: '100%',
                borderRadius: { xs: 0, sm: 1 },
                borderLeft: { xs: 0, sm: undefined },
                borderRight: { xs: 0, sm: undefined },
              }}
            >
              <Table
                size="small"
                stickyHeader={isLargeScreen}
                sx={{
                  'width': '100%',
                  'tableLayout': { lg: 'auto' },
                  '& .team-actions-cell': {
                    width: 48,
                    paddingRight: { xs: 0.5, sm: 1 },
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableHead sx={{ display: { xs: 'none', lg: 'table-header-group' } }}>
                  <TableRow>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('organization.team.columnRelation')}</TableCell>
                    <TableCell>{t('organization.team.columnRoles')}</TableCell>
                    <TableCell>{t('common.relationStatus')}</TableCell>
                    <TableCell align="right" className="team-actions-cell">
                      <Typography component="span" variant="caption" color="text.secondary">
                        {t('organization.team.columnActions')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={isLargeScreen ? 5 : 1}>
                        <Typography variant="body2" sx={{ px: { xs: 1, sm: 0 } }}>
                          …
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) =>
                      isLargeScreen ? (
                        <TableRow key={row.id} hover>
                          <TableCell>{memberDisplayName(row)}</TableCell>
                          <TableCell>{formatBasicRolesCell(row, t)}</TableCell>
                          <TableCell>{formatElevatedRolesCell(row, t)}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell align="right" className="team-actions-cell">
                            <OrganizationTeamMemberRowMenu
                              canManageRoles={canManageRoles}
                              isActiveMember={row.status === 'active'}
                              onManageRoles={() => openRolesDialog(row)}
                              onOpenHistory={() => setHistoryRelationId(row.id)}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={row.id} hover>
                          <TableCell
                            sx={{
                              px: { xs: 1, sm: 2 },
                              py: 1.5,
                              verticalAlign: 'top',
                              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                              <Stack spacing={0.75} minWidth={0} flex={1}>
                                <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                  {memberDisplayName(row)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {t('organization.team.columnRelation')}
                                  </Box>
                                  {': '}
                                  {formatBasicRolesCell(row, t)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {t('organization.team.columnRoles')}
                                  </Box>
                                  {': '}
                                  {formatElevatedRolesCell(row, t)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {t('common.relationStatus')}
                                  </Box>
                                  {': '}
                                  {row.status}
                                </Typography>
                              </Stack>
                              <OrganizationTeamMemberRowMenu
                                canManageRoles={canManageRoles}
                                isActiveMember={row.status === 'active'}
                                onManageRoles={() => openRolesDialog(row)}
                                onOpenHistory={() => setHistoryRelationId(row.id)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>

      <InviteModal open={inviteModalOpen} close={() => setInviteModalOpen(false)} />

      <Dialog open={!!rolesDialogRow} onClose={() => setRolesDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('organization.team.rolesDialogTitle')}</DialogTitle>
        <DialogContent>
          {rolesDialogRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {memberDisplayName(rolesDialogRow)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('organization.team.memberImpliedNote')}
              </Typography>
              {isSoleActiveOwnerRow ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t('organization.team.soleOwnerOwnerLocked')}
                </Alert>
              ) : null}
              {rolesDialogDelta.removed.size > 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t('organization.team.rolesRemovalEmailNotice')}
                </Alert>
              ) : null}
              {rolesDialogDelta.added.size > 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t('organization.team.rolesAdditionInviteNotice')}
                </Alert>
              ) : null}
              {/* ── Seat-assignment section ─────────────────────────────── */}
              {balancesData && balancesData.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t('organization.team.seatAssignmentsTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('organization.team.seatAssignmentsHint')}
                  </Typography>
                  {balancesData
                    .filter((b) => b.totalGranted > 0)
                    .map((balance) => {
                      const currentlyAssigned = !!(rolesDialogRow?.quotaAssignments ?? []).find(
                        (a) => a.quotaKey === balance.quotaKey,
                      );
                      const isDraftChecked = seatDraft[balance.quotaKey] ?? currentlyAssigned;
                      const isNewlyChecked = isDraftChecked && !currentlyAssigned;
                      const isNewlyUnchecked = !isDraftChecked && currentlyAssigned;
                      const effectiveAvailable =
                        balance.available - (isNewlyChecked ? 1 : 0) + (isNewlyUnchecked ? 1 : 0);
                      const wouldExceedCapacity = isNewlyChecked && balance.available <= 0;
                      const shortKey = balance.quotaKey.includes(':')
                        ? balance.quotaKey.split(':').pop()!
                        : balance.quotaKey;
                      const label = t(`subscription.organization.planPage.quota.${shortKey}`, {
                        defaultValue: shortKey.charAt(0).toUpperCase() + shortKey.slice(1),
                      });
                      return (
                        <Box key={balance.quotaKey}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isDraftChecked}
                                disabled={patchRoles.isPending}
                                onChange={(_, checked) => {
                                  setSeatDraft((prev) => ({ ...prev, [balance.quotaKey]: checked }));
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {t('organization.team.seatQuotaRowLabel', { quotaLabel: label })}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={t('organization.team.seatAvailabilityChip', {
                                    available: effectiveAvailable,
                                    total: balance.totalGranted,
                                  })}
                                  color={effectiveAvailable <= 0 && !isDraftChecked ? 'warning' : 'default'}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                          />
                          {wouldExceedCapacity ? (
                            <Alert severity="warning" sx={{ py: 0.25, ml: 4, mt: -0.5 }}>
                              {t('organization.team.seatNoAvailability')}
                            </Alert>
                          ) : null}
                        </Box>
                      );
                    })}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {ORG_ELEVATED_CATEGORY_ORDER.map((category) => {
                  const rolesInSection = [...ORG_ELEVATED_SECTION_ROLES[category]];
                  const value = draftByCategory[category] ?? '';
                  const allowNoneForCategory = category !== 'management' || !isSoleActiveOwnerRow;
                  // Capacity-axis: any seat checked in draft unlocks operational roles
                  const hasAnySeat = (balancesData ?? [])
                    .filter((b) => b.totalGranted > 0)
                    .some((b) => {
                      const currentlyAssigned = !!(rolesDialogRow?.quotaAssignments ?? []).find(
                        (a) => a.quotaKey === b.quotaKey,
                      );
                      return seatDraft[b.quotaKey] ?? currentlyAssigned;
                    });
                  // Feature-axis: active feature keys from org entitlements
                  const activeFeatureKeys = new Set(orgFeatureKeys ?? []);
                  return (
                    <Accordion key={category} disableGutters defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">{t(`organization.team.roleSections.${category}`)}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormControl>
                          <FormLabel sx={{ mb: 1, fontSize: '0.85rem' }}>
                            {t('organization.team.roleSectionPickLabel')}
                          </FormLabel>
                          <RadioGroup
                            value={value}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCategoryElevated(category, v === '' ? '' : (v as OrganizationRole));
                            }}
                          >
                            {allowNoneForCategory ? (
                              <FormControlLabel
                                value=""
                                control={<Radio size="small" />}
                                label={t('organization.team.roleNone')}
                                disabled={patchRoles.isPending}
                              />
                            ) : null}
                            {rolesInSection.map((role) => {
                              const isCapacityRole = CAPACITY_AXIS_ROLES.has(role);
                              const featureKey = FEATURE_KEY_FOR_ROLE[role];
                              const isFeatureRole = !!featureKey;
                              const disableAdminWhenSoleOwner =
                                isSoleActiveOwnerRow && category === 'management' && role === ORGANIZATION_ROLES.ADMIN;
                              const capacityBlocked = isCapacityRole && !hasAnySeat;
                              const featureBlocked = isFeatureRole && !activeFeatureKeys.has(featureKey!);
                              const isDisabled =
                                patchRoles.isPending || disableAdminWhenSoleOwner || capacityBlocked || featureBlocked;
                              const tooltipText = capacityBlocked
                                ? t('organization.team.requiresSeat', { defaultValue: 'Requires a Member seat' })
                                : featureBlocked
                                  ? t('organization.team.requiresFeaturePlan', {
                                      defaultValue: 'Not available on your current plan',
                                    })
                                  : '';
                              return (
                                <Tooltip
                                  key={role}
                                  title={tooltipText}
                                  placement="right"
                                  disableHoverListener={!tooltipText}
                                >
                                  <span>
                                    <FormControlLabel
                                      value={role}
                                      control={<Radio size="small" />}
                                      label={organizationRoleLabel(role, t)}
                                      disabled={isDisabled}
                                    />
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </RadioGroup>
                        </FormControl>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolesDialogRow(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleSaveRoles()}
            disabled={
              patchRoles.isPending ||
              !hasAnyDialogChanges ||
              // Disable if a newly-checked seat has no available quota
              (balancesData ?? []).some((b) => {
                const currentlyAssigned = !!(rolesDialogRow?.quotaAssignments ?? []).find(
                  (a) => a.quotaKey === b.quotaKey,
                );
                const isDraftChecked = seatDraft[b.quotaKey] ?? currentlyAssigned;
                return isDraftChecked && !currentlyAssigned && b.available <= 0;
              })
            }
          >
            {t('organization.team.applyRoles')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historyRelationId} onClose={() => setHistoryRelationId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.relationHistory')}</DialogTitle>
        <DialogContent>
          {eventsLoading ? (
            <Typography variant="body2">…</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(eventsData?.data ?? []).map((ev) => (
                <Paper key={ev.id} variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {ev.eventType} · {new Date(ev.occurredAt).toLocaleString()}
                  </Typography>
                  {ev.payload ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {JSON.stringify(ev.payload)}
                    </Typography>
                  ) : null}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
